import { InfoCircleIcon, SearchSmIcon } from '@langchain/untitled-ui-icons';
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import Box from '@mui/joy/Box';

import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { SetHandleModal } from '@/Pages/Hub/SetHandle';
import { ChooseWebhooks } from '@/Pages/Playground/components/ChooseWebhooks';
import { PromptWebhook } from '@/Pages/Prompts/components/Webhooks/types';
import { Button, Tooltip } from '@/design-system';
import { useSelectedTenant } from '@/hooks/useSwr';
import { ResourceTag } from '@/types/schema';

import { InfoTooltip } from '../InfoTooltip/InfoTooltip';
import { ResourceTagCrudPaneEditor } from '../ResourceTagFilter/ResourceTagCrudPaneEditor';
import { TAGS_CATEGORY, tagsByCategory } from './constants';

type RepoType = {
  owner: string;
  repo_handle: string;
  description: string;
  readme: string;
  is_public: boolean;
  is_archived: boolean;
  tags: string[];
};
export function useRepoState(
  initialState?: RepoType
): [RepoType, (repo: Partial<RepoType>) => void] {
  const [state, setState] = useState<RepoType>(
    initialState ?? {
      owner: '',
      repo_handle: '',
      description: '',
      readme: '',
      is_public: false,
      is_archived: false,
      tags: [],
    }
  );
  const setRepo = (repo: Partial<RepoType>) => {
    setState((state) => ({ ...state, ...repo }));
  };

  const { data: selectedTenant } = useSelectedTenant();
  const handle = selectedTenant?.tenant_handle;
  useEffect(() => {
    if (handle && !state.owner) setRepo({ owner: handle });
  }, [handle, state.owner]);
  return [state, setRepo];
}

export function RepoForm({
  repo,
  setRepo,
  onSubmit,
  onCancel,
  isLoading,
  isValidatingPrompts,
  mode,
  error,
  hideTitle,
  minimal,
  tagsOnResource,
  setTagsOnResource,
  webhooks,
  excludedWebhookIds,
  setExcludedWebhookIds,
}: {
  repo: RepoType;
  setRepo: (repo: Partial<RepoType>) => void;
  tenantHandle: string | undefined;
  onSubmit: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  mode: 'create' | 'edit' | 'fork';
  error?: Error | null;
  isValidatingPrompts?: boolean;
  hideTitle?: boolean;
  minimal?: boolean;
  tagsOnResource?: ResourceTag[];
  setTagsOnResource?: Dispatch<SetStateAction<ResourceTag[]>>;
  webhooks?: PromptWebhook[];
  excludedWebhookIds?: string[];
  setExcludedWebhookIds?: Dispatch<SetStateAction<string[]>>;
}) {
  const tagsMap = useMemo(() => {
    const map = {};
    for (const tag of repo.tags) {
      const category =
        tagsByCategory.find((i) => i.tags.includes(tag))?.category ??
        TAGS_CATEGORY;
      map[category] = [...(map[category] ?? []), tag];
    }
    return map;
  }, [repo.tags]);

  const { data: selectedTenant } = useSelectedTenant();
  const isPersonal = selectedTenant?.is_personal;

  const [setHandleOpen, setSetHandleOpen] = useState(false);

  const innerOnSubmit = async (e) => {
    e.preventDefault();
    if (!repo.owner && repo.is_public) {
      setSetHandleOpen(true);
    } else {
      await onSubmit();
    }
  };

  return (
    <>
      <form onSubmit={innerOnSubmit}>
        <Box display="flex" alignItems={'center'} paddingY={1}>
          <Box flexGrow={1}></Box>
          <Grid
            container
            flexBasis={600}
            flexGrow={0}
            flexShrink={1}
            spacing={4}
            sx={{ textAlign: 'left' }}
          >
            {!hideTitle && (
              <Grid xs={12}>
                <Typography level="h2">
                  {mode === 'create'
                    ? 'Create a New Prompt'
                    : `${mode === 'fork' ? 'Fork' : 'Update'} ${
                        repo.owner ? `${repo.owner}/` : ''
                      }${repo.repo_handle}`}
                </Typography>
              </Grid>
            )}
            <Grid sx={{ width: '100%' }}>
              <FormControl error={!!error}>
                <FormLabel>
                  <div className="flex flex-row gap-2">
                    <div className="flex flex-row items-center gap-2">
                      <div className="text-base font-medium">Prompt name</div>
                      {mode === 'create' && (
                        <InfoTooltip
                          title={
                            'By default, prompts are only accessible in the workspace they are created in.'
                          }
                        />
                      )}
                      {mode === 'edit' && (
                        <Tooltip title="Prompt names are not editable after creation">
                          <div>
                            <InfoCircleIcon className="my-auto h-3.5 w-3.5 text-tertiary" />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </FormLabel>
                <Stack direction="row" spacing={0.5}>
                  {repo.owner && repo.is_public && (
                    <div className="my-auto">{repo.owner}/</div>
                  )}
                  <Input
                    data-testid="prompt-name-input"
                    fullWidth
                    value={repo.repo_handle}
                    onChange={(e) => {
                      setRepo({ repo_handle: e.target.value });
                    }}
                    disabled={mode === 'edit'}
                    readOnly={mode === 'edit'}
                    endDecorator={
                      isValidatingPrompts ? (
                        <CircularProgress size="sm" />
                      ) : (
                        <div style={{ width: 20, height: 20 }} />
                      )
                    }
                  />
                </Stack>
                <FormHelperText>
                  {error && (
                    <Typography color="danger" level="body2">
                      {error.message}
                    </Typography>
                  )}
                </FormHelperText>
              </FormControl>
            </Grid>
            {tagsOnResource &&
              setTagsOnResource &&
              !repo.is_public &&
              !isPersonal && (
                <Grid sx={{ width: '100%' }}>
                  <FormControl>
                    <ResourceTagCrudPaneEditor
                      tagsOnResource={tagsOnResource}
                      setTagsOnResource={setTagsOnResource}
                    />
                  </FormControl>
                </Grid>
              )}

            {webhooks &&
            webhooks.length > 0 &&
            excludedWebhookIds &&
            setExcludedWebhookIds ? (
              <Grid xs={12}>
                <ChooseWebhooks
                  webhooks={webhooks}
                  excludedWebhookIds={excludedWebhookIds}
                  setExcludedWebhookIds={setExcludedWebhookIds}
                />
              </Grid>
            ) : null}

            {!minimal && (
              <>
                <Grid xs={12}>
                  <FormLabel>
                    <Typography level="body1">Description</Typography>
                  </FormLabel>
                  <Textarea
                    value={repo.description}
                    minRows={2}
                    onChange={(e) => setRepo({ description: e.target.value })}
                  />
                </Grid>

                <Grid xs={12}>
                  <FormLabel>
                    <Typography level="body1">Readme</Typography>
                  </FormLabel>
                  <Textarea
                    value={repo.readme}
                    minRows={4}
                    onChange={(e) => setRepo({ readme: e.target.value })}
                  />
                  <FormHelperText>
                    <Typography level="body2">
                      Use markdown for formatting
                    </Typography>
                  </FormHelperText>
                </Grid>

                {tagsByCategory
                  .filter((i) => i.category !== 'Type')
                  .map((i) => (
                    <Grid xs={12} key={i.category}>
                      <FormControl>
                        <FormLabel>
                          <Typography level="body1">{i.category}</Typography>
                        </FormLabel>

                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          placeholder={`Enter ${
                            i.category == 'Language' ? 'a ' : ''
                          }${i.category.toLowerCase()}...`}
                          startDecorator={<SearchSmIcon className="size-4" />}
                          options={i.tags}
                          value={tagsMap[i.category] ?? []}
                          freeSolo
                          slotProps={{
                            listbox: {
                              modifiers: [
                                {
                                  name: 'flip',
                                  enabled: false,
                                },
                              ],
                            },
                          }}
                          onChange={(_, newValue) => {
                            const prev = tagsMap[i.category] ?? [];
                            const added = newValue.filter(
                              (i) => !prev.includes(i)
                            );
                            const removed = prev.filter(
                              (i) => !newValue.includes(i)
                            );

                            setRepo({
                              tags: [...repo.tags, ...added].filter(
                                (i) => !removed.includes(i)
                              ),
                            });
                          }}
                        />
                      </FormControl>
                    </Grid>
                  ))}

                {repo.is_public && (
                  <>
                    <Grid xs={12} textAlign={'left'}>
                      <Typography level="h4">Sharing Settings</Typography>
                      <p>Manage who can see your repos</p>
                    </Grid>
                    <Grid xs={12} textAlign={'left'}>
                      <FormLabel>
                        <Typography level="body1">License</Typography>
                      </FormLabel>
                      <Autocomplete
                        placeholder="Select a license"
                        disabled
                        options={['MIT']}
                        value={'MIT'}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}

            {mode === 'edit' && (
              <>
                <Grid xs={12} textAlign={'left'}>
                  <Typography level="h4">Archive Settings</Typography>
                  <p>Clean up prompts that aren't in use anymore</p>
                </Grid>
                <Grid xs={12} textAlign={'left'}>
                  <RadioGroup
                    value={repo.is_archived}
                    onChange={(e) =>
                      setRepo({ is_archived: e.target.value === 'true' })
                    }
                  >
                    <Radio
                      value={'false'}
                      label={
                        <>
                          <Typography level="body1">Normal</Typography>{' '}
                          <Typography level="body2">
                            This prompt is discoverable
                          </Typography>
                        </>
                      }
                    />
                    <Radio
                      value={'true'}
                      label={
                        <>
                          <Typography level="body1">Archived</Typography>{' '}
                          <Typography level="body2">
                            This prompt will not show up in default searches
                          </Typography>
                        </>
                      }
                    />
                  </RadioGroup>
                </Grid>
              </>
            )}
            <div className="mx-4 flex w-full flex-row justify-end gap-2">
              {onCancel && (
                <Button variant="outlined" onClick={onCancel} size="md">
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={isLoading || !!error || isValidatingPrompts}
                size="md"
                loading={isLoading}
              >
                {mode === 'fork' ? 'Fork' : 'Save'}
              </Button>
            </div>
          </Grid>
          <Box flexGrow={1}></Box>
        </Box>
      </form>
      <SetHandleModal
        open={setHandleOpen}
        onClose={() => setSetHandleOpen(false)}
      />
    </>
  );
}
